import type { DeclaredMemberType } from "@ondo/shared";
import { type FormEvent, useId } from "react";
import { Link } from "react-router";

import { MEMBERSHIP_TYPES } from "../lib/constants.ts";
import { PATHS } from "../routes/routes.constants.ts";
import { COUNTRIES } from "./signup.constants.ts";
import type { DetailsValues } from "./signup.model.ts";

type Props = {
  values: DetailsValues;
  passwordsMatch: boolean;
  asksMembership: boolean;
  error: string | null;
  busy: boolean;
  onChange: <K extends keyof DetailsValues>(field: K, value: DetailsValues[K]) => void;
  onToggleMembership: (type: DeclaredMemberType) => void;
  onSubmit: () => void;
};

export function DetailsForm({
  values,
  passwordsMatch,
  asksMembership,
  error,
  busy,
  onChange,
  onToggleMembership,
  onSubmit,
}: Props) {
  const id = useId();

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit();
  }

  return (
    <form className="signup__form" onSubmit={submit} noValidate>
      <div className="signup__field">
        <label className="signup__label" htmlFor={`${id}-name`}>
          Full name *
        </label>
        <input
          id={`${id}-name`}
          className="signup__input"
          autoComplete="name"
          placeholder="Your full name"
          value={values.fullName}
          disabled={busy}
          onChange={(event) => onChange("fullName", event.target.value)}
        />
      </div>

      <div className="signup__pair">
        <div className="signup__field">
          <label className="signup__label" htmlFor={`${id}-email`}>
            Email *
          </label>
          <input
            id={`${id}-email`}
            className="signup__input"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={values.email}
            disabled={busy}
            onChange={(event) => onChange("email", event.target.value)}
          />
        </div>

        <div className="signup__field">
          <label className="signup__label" htmlFor={`${id}-phone`}>
            Phone *
          </label>
          <input
            id={`${id}-phone`}
            className="signup__input"
            type="tel"
            autoComplete="tel"
            placeholder="+44 7700…"
            value={values.phone}
            disabled={busy}
            onChange={(event) => onChange("phone", event.target.value)}
          />
        </div>
      </div>

      <div className="signup__pair">
        <div className="signup__field">
          <label className="signup__label" htmlFor={`${id}-password`}>
            Password *
          </label>
          <input
            id={`${id}-password`}
            className="signup__input"
            type="password"
            autoComplete="new-password"
            placeholder="Min. 8 characters"
            value={values.password}
            disabled={busy}
            onChange={(event) => onChange("password", event.target.value)}
          />
        </div>

        <div className="signup__field">
          <label className="signup__label" htmlFor={`${id}-confirm`}>
            Confirm password *
          </label>
          <input
            id={`${id}-confirm`}
            className={`signup__input${
              passwordsMatch ? " signup__input--matched" : ""
            }`}
            type="password"
            autoComplete="new-password"
            placeholder="Repeat password"
            value={values.confirmPassword}
            disabled={busy}
            onChange={(event) => onChange("confirmPassword", event.target.value)}
          />
          {passwordsMatch && (
            <span className="signup__match">✓ Passwords match</span>
          )}
        </div>
      </div>

      <div className="signup__field">
        <label className="signup__label" htmlFor={`${id}-country`}>
          Country of residence *
        </label>
        <select
          id={`${id}-country`}
          className="signup__input signup__select"
          value={values.country}
          disabled={busy}
          onChange={(event) => onChange("country", event.target.value)}
        >
          {COUNTRIES.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
      </div>

      {/*
        Checkboxes, because these are not exclusive — a gallery director
        collects, an estate consigns to auction. And real inputs rather than
        the reference build's <button>s, which look identical but announce as
        eight unrelated controls with no grouping and no checked state.

        ABSENT ENTIRELY on an invited sign-up, rather than present and optional.
        The eight are art-trade descriptions and the invited applicant is a
        haulier, a registrar, a shipping clerk — so a group they are meant to
        leave blank would read as a question they had failed to answer, and the
        likeliest outcome is that they tick one to get past it. Whether it is
        asked is the controller's decision; the view only renders it.
      */}
      {asksMembership ? (
      <fieldset
        className="signup__fieldset"
        aria-describedby={`${id}-membership-hint`}
      >
        <legend className="signup__label">Membership type</legend>
        {/* On the fieldset, not the wrapper div: a div has no role for a
            description to attach to, so it would be announced nowhere. */}
        <p className="signup__hint" id={`${id}-membership-hint`}>
          Select all that apply.
        </p>
        <div className="signup__choices">
          {MEMBERSHIP_TYPES.map((type) => (
            <label
              key={type.value}
              className={`signup__choice${
                values.declaredTypes.includes(type.value) ? " signup__choice--on" : ""
              }`}
            >
              <input
                className="signup__choice-input"
                type="checkbox"
                value={type.value}
                checked={values.declaredTypes.includes(type.value)}
                disabled={busy}
                onChange={() => onToggleMembership(type.value)}
              />
              {type.label}
            </label>
          ))}
        </div>
      </fieldset>
      ) : null}

      <fieldset className="signup__callout">
        <legend className="signup__callout-title">Would you like to book a call?</legend>
        <p className="signup__callout-body">
          We can walk you through the platform and answer any questions before you
          get started.
        </p>
        <div className="signup__callout-choices">
          {[
            { value: true, label: "Yes, book a call" },
            { value: false, label: "No thanks" },
          ].map((option) => (
            <label
              key={option.label}
              className={`signup__choice${
                values.bookCall === option.value ? " signup__choice--on" : ""
              }`}
            >
              <input
                className="signup__choice-input"
                type="radio"
                name={`${id}-call`}
                checked={values.bookCall === option.value}
                disabled={busy}
                onChange={() => onChange("bookCall", option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>
        {values.bookCall === true && (
          <p className="signup__callout-note">
            ✓ We&rsquo;ll send a scheduling link to{" "}
            <strong>{values.email || "your email"}</strong> for a 30-minute
            onboarding call.
          </p>
        )}
      </fieldset>

      {error && (
        <p className="signup__error" role="alert">
          {error}
        </p>
      )}

      <button type="submit" className="signup__submit" disabled={busy}>
        Continue to Identity Verification →
      </button>

      <p className="signup__footnote">
        Already have an account?{" "}
        <Link className="signup__link" to={PATHS.signIn}>
          Sign in
        </Link>
      </p>
    </form>
  );
}
